/**
 * ============================================================================
 * CENTRALIZED ROUTE & COMPONENT CONFIGURATION
 * ============================================================================
 *
 * This is the SINGLE SOURCE OF TRUTH for:
 * - Route paths
 * - Components
 * - Permissions
 * - Menu display
 *
 * Define a route ONCE here, use it EVERYWHERE automatically!
 * ============================================================================
 */

import { lazy } from 'react';
import { UserRole } from './roles';
/**
 * Route Configuration Interface
 * Only defines route, component, and permissions
 * Menu configuration is handled in Sidebar.tsx
 */
export interface RouteConfig {
  path: string;
  component: React.LazyExoticComponent<any> | (() => JSX.Element);
  allowedRoles: UserRole[];
  isPublic?: boolean;
}

/**
 * ============================================================================
 * LAZY-LOADED COMPONENTS
 * ============================================================================
 */

// Auth Pages
const Login = lazy(() => import('@pages/auth/Login'));
const ResetPassword = lazy(() => import('@pages/auth/ResetPassword'));
const Unauthorized = lazy(() => import('@pages/auth/Unauthorized'));
const RoleSelection = lazy(() => import('@pages/auth/RoleSelection'));

// Admin Pages
const UserManagement = lazy(() => import('@pages/admin/UserManagement'));
const ManageUsers = lazy(() => import('@pages/admin/ManageUsers'));
const ManageOrganizationalAdmins = lazy(() => import('@pages/admin/ManageOrganizationalAdmins'));
const CreateOrgAdmin = lazy(() => import('@pages/admin/CreateOrgAdmin'));
const ViewOrgAdmin = lazy(() => import('@pages/admin/ViewOrgAdmin'));
const AdminDashboard = lazy(() => import('@pages/admin/AdminDashboard'));
const RecentActivities = lazy(() => import('@pages/admin/RecentActivities'));
const NotificationsPage = lazy(() => import('@pages/admin/NotificationsPage'));
const ReportsPage = lazy(() => import('@pages/admin/ReportsPage'));
const SuperAdminDashboard = lazy(() => import('@pages/admin/SuperAdminDashboard'));

// Profile
const UserProfileSettings = lazy(() => import('@pages/profile/UserProfileSettings'));

// Routers
const DashboardRouter = lazy(() => import('@components/DashboardRouter/DashboardRouter'));
const DailyLogRouter = lazy(() => import('@components/DailyLogRouter/DailyLogRouter'));
const HolidayRouter = lazy(() => import('@components/HolidayRouter/HolidayRouter'));
const AttendanceHistoryRouter = lazy(() => import('@components/AttendanceHistoryRouter/AttendanceHistoryRouter'));
const HolidayCalendarViewRouter = lazy(() => import('@components/HolidayCalendarViewRouter/HolidayCalendarViewRouter'));

// Program Manager
const EnhancedProgramList = lazy(() => import('@modules/programManager/components/EnhancedProgramList'));
const ChecklistPage = lazy(() => import('@modules/programManager/components/ChecklistPage'));
const ProgramProgressView = lazy(() => import('@modules/programManager/components/ProgramProgressView'));
const ChecklistManagement = lazy(() => import('@modules/programManager/components/ChecklistManagement'));
const TaskManagement = lazy(() => import('@modules/programManager/components/TaskManagement'));

// Workshops
const WorkshopsPage = lazy(() => import('@pages/workshops/WorkshopsPage'));
const WorkshopTasksPage = lazy(() => import('@pages/workshops/WorkshopTasksPage'));

// HR Pages
const EmployeesPage = lazy(() => import('@pages/hr/EmployeesPage'));
const EmployeeEditPage = lazy(() => import('@pages/hr/EmployeeEditPage'));
const EmployeeProfilePage = lazy(() => import('@pages/hr/EmployeeProfilePage'));
const DailyTrackerPage = lazy(() => import('@pages/hr/DailyTrackerPage'));
const DailyUpdatesFeedPage = lazy(() => import('@pages/hr/DailyUpdatesFeedPage'));
const LeavesPage = lazy(() => import('@pages/hr/LeavesPage'));
const HRProfileSettings = lazy(() => import('@pages/hr/HRProfileSettings'));
const HRHolidayList = lazy(() => import('@pages/hr/HRHolidayList'));
const HRHolidayCalendarView = lazy(() => import('@pages/hr/HRHolidayCalendarView'));
const HolidayManagement = lazy(() => import('@pages/hr/HolidayManagement'));
const Payroll = lazy(() => import('@/pages/hr/PayrollManagement/PayRoll'));
const PayrollDetails = lazy(() => import('@/pages/hr/PayrollManagement/PayRollDetails'));
const AddEmployeePayroll = lazy(() => import('@/pages/hr/PayrollManagement/AddEmployeePayroll'));
const EditEmployeePayroll = lazy(() => import('@/pages/hr/PayrollManagement/EditEmployeePayroll'));
const ViewEmployeeSalarySlip = lazy(() => import('@/pages/hr/PayrollManagement/ViewEmployeeSalarySlip'));
const JobPostingsPage = lazy(() => import('@pages/hr/JobPostingsPage'));
const JobPostingFormPage = lazy(() => import('@pages/hr/JobPostingFormPage'));
const RecognitionsPage = lazy(() => import('@pages/hr/RecognitionsPage'));
const JobApplicationsPage = lazy(() => import('@pages/hr/JobApplicationsPage'));
const LeavePolicySettings = lazy(() => import('@pages/hr/LeavePolicySettings'));
const WallOfFamePage = lazy(() => import('@pages/common/WallOfFamePage'));

// Public Pages (no auth required)
const PublicJobsPage = lazy(() => import('@pages/public/PublicJobsPage'));
const PublicJobDetailPage = lazy(() => import('@pages/public/PublicJobDetailPage'));
const VerifyPayslip = lazy(() => import('@pages/public/VerifyPayslip'));
const PlanBoardPage = lazy(() => import('@pages/hr/planReports/PlanBoardPage'));
const PeriodReportPage = lazy(() => import('@pages/hr/planReports/PeriodReportPage'));
const SlippagePage = lazy(() => import('@pages/hr/planReports/SlippagePage'));
const AnalyticsPage = lazy(() => import('@pages/hr/planReports/AnalyticsPage'));
const MyWork = lazy(() => import('@pages/employee/MyWork'));
const LeavePortal = lazy(() => import('@pages/employee/LeavePortal'));
const AttendanceHistory = lazy(() => import('@pages/employee/AttendanceHistory'));
const TeamAttendance = lazy(() => import('@pages/employee/TeamAttendance'));
const HolidayList = lazy(() => import('@pages/employee/HolidayList'));
const HolidayCalendarView = lazy(() => import('@pages/employee/HolidayCalendarView'));
const EmployeeResources = lazy(() => import('@pages/employee/EmployeeResources'));

const HRDailyLog = lazy(() => import('@pages/hr/HRDailyLog'));
const HRAttendanceHistory = lazy(() => import('@pages/hr/HRAttendanceHistory'));

// Program Manager Holiday Pages
const PMHolidayList = lazy(() => import('@pages/programManager/HolidayList'));
const PMHolidayCalendarView = lazy(() => import('@pages/programManager/HolidayCalendarView'));

// Admin Holiday Pages
const AdminHolidayList = lazy(() => import('@pages/admin/AdminHolidayList'));
const AdminHolidayCalendarView = lazy(() => import('@pages/admin/AdminHolidayCalendarView'));

// Program Manager Daily Log
const PMDailyLog = lazy(() => import('@pages/programManager/DailyLog'));
const PMAttendanceHistory = lazy(() => import('@pages/programManager/PMAttendanceHistory'));

// Admin Daily Log
const AdminDailyLog = lazy(() => import('@pages/admin/DailyLog'));
const AdminAttendanceHistory = lazy(() => import('@pages/admin/AdminAttendanceHistory'));

// Assessment Creator Pages
const AssessmentDashboard = lazy(() => import('@pages/assessmentCreator/AssessmentDashboard'));

// Products Pages
const MagnetixContactSubmissions = lazy(() => import('@pages/products/MagnetixContactSubmissions'));
const ManageAssessments = lazy(() => import('@pages/assessmentCreator/ManageAssessments'));
const AssessmentResults = lazy(() => import('@pages/assessmentCreator/AssessmentResults'));
const CreateAssessment = lazy(() => import('@pages/assessmentCreator/CreateAssessment'));
const ModuleAssessmentMapping = lazy(() => import('@pages/assessmentCreator/ModuleAssessmentMapping'));
const CreateQuestion = lazy(() => import('@pages/assessmentCreator/CreateQuestion'));
const Users = lazy(() => import('@pages/assessmentCreator/Users'));
const ManageDimensionsModules = lazy(() => import('@pages/assessmentCreator/ManageDimensionsModules'));
const ManageAssessmentEligibility = lazy(() => import('@pages/assessmentCreator/ManageAssessmentEligibility'));
const ManageAssessmentDomains = lazy(() => import('@pages/assessmentCreator/ManageAssessmentDomains'));
const ReportingManagerManagement = lazy(() => import('@pages/assessmentCreator/feedback360/ReportingManagerManagement'));
const ReporteeManagement = lazy(() => import('@pages/assessmentCreator/feedback360/ReporteeManagement'));

/**
 * ============================================================================
 * ALL ROUTES CONFIGURATION
 * ============================================================================
 * Define your route ONCE with all its properties.
 * This configuration is used for:
 * - App.tsx routing
 * - Sidebar menu generation
 * - Permission checking
 * - Everything!
 */
export const ROUTES: RouteConfig[] = [
  // ========================================================================
  // PUBLIC ROUTES
  // ========================================================================
  {
    path: '/login',
    component: Login,
    allowedRoles: [],
    isPublic: true,
  },
  {
    path: '/reset-password',
    component: ResetPassword,
    allowedRoles: [],
    isPublic: true,
  },
  {
    path: '/unauthorized',
    component: Unauthorized,
    allowedRoles: [],
    isPublic: true,
  },

  // ========================================================================
  // COMMON ROUTES (All Authenticated Users)
  // ========================================================================
  {
    path: '/dashboard',
    component: DashboardRouter,
    allowedRoles: [
      UserRole.ADMIN,
      UserRole.PROGRAM_MANAGER,
      UserRole.ASSESSMENT_CREATOR,
      UserRole.HR,
      UserRole.EMPLOYEE,
    ],
  },
  {
    path: '/daily-log',
    component: DailyLogRouter,
    allowedRoles: [
      UserRole.ADMIN,
      UserRole.PROGRAM_MANAGER,
      UserRole.HR,
      UserRole.EMPLOYEE,
    ],
  },
  {
    path: '/holidays',
    component: HolidayRouter,
    allowedRoles: [
      UserRole.ADMIN,
      UserRole.PROGRAM_MANAGER,
      UserRole.HR,
      UserRole.EMPLOYEE,
    ],
  },
  {
    path: '/attendance-history',
    component: AttendanceHistoryRouter,
    allowedRoles: [
      UserRole.ADMIN,
      UserRole.PROGRAM_MANAGER,
      UserRole.HR,
      UserRole.EMPLOYEE,
    ],
  },
  {
    path: '/holiday-calendar-view',
    component: HolidayCalendarViewRouter,
    allowedRoles: [
      UserRole.ADMIN,
      UserRole.PROGRAM_MANAGER,
      UserRole.HR,
      UserRole.EMPLOYEE,
    ],
  },
  {
    path: '/role-selection',
    component: RoleSelection,
    allowedRoles: [
      UserRole.ADMIN,
      UserRole.PROGRAM_MANAGER,
      UserRole.ASSESSMENT_CREATOR,
      UserRole.HR,
      UserRole.EMPLOYEE,
    ],
  },
  {
    path: '/profile/settings',
    component: UserProfileSettings,
    allowedRoles: [
      UserRole.ADMIN,
      UserRole.PROGRAM_MANAGER,
      UserRole.ASSESSMENT_CREATOR,
      UserRole.HR,
      UserRole.EMPLOYEE,
    ],
  },
  {
    path: '/role-decider',
    component: SuperAdminDashboard,
    allowedRoles: [UserRole.ADMIN, UserRole.PROGRAM_MANAGER, UserRole.HR, UserRole.EMPLOYEE, UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/organization-feed',
    component: lazy(() => import('@pages/OrganizationFeed/OrganizationFeedPage')),
    allowedRoles: [UserRole.ADMIN, UserRole.PROGRAM_MANAGER, UserRole.HR, UserRole.EMPLOYEE, UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/organization',
    component: lazy(() => import('@pages/OrganizationSetup/OrganizationSetupPage')),
    allowedRoles: [UserRole.ADMIN, UserRole.PROGRAM_MANAGER, UserRole.HR, UserRole.EMPLOYEE, UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/organization/assign-leader',
    component: lazy(() => import('@pages/OrganizationSetup/AssignLeaderPage')),
    allowedRoles: [UserRole.ADMIN, UserRole.HR],
  },
  {
    path: '/organization/assign-leader/:id',
    component: lazy(() => import('@pages/OrganizationSetup/AssignLeaderPage')),
    allowedRoles: [UserRole.ADMIN, UserRole.HR],
  },

  // ========================================================================
  // OKR ROUTES (All Authenticated Users)
  // ========================================================================
  {
    path: '/okr',
    component: lazy(() => import('@modules/okr/pages/OKRDashboard')),
    allowedRoles: [
      UserRole.ADMIN,
      UserRole.PROGRAM_MANAGER,
      UserRole.ASSESSMENT_CREATOR,
      UserRole.HR,
      UserRole.EMPLOYEE,
    ],
  },
  {
    path: '/okr/my',
    component: lazy(() => import('@modules/okr/pages/MyOKRs')),
    allowedRoles: [
      UserRole.ADMIN,
      UserRole.PROGRAM_MANAGER,
      UserRole.ASSESSMENT_CREATOR,
      UserRole.HR,
      UserRole.EMPLOYEE,
    ],
  },
  {
    path: '/okr/team',
    component: lazy(() => import('@modules/okr/pages/TeamOKRs')),
    allowedRoles: [
      UserRole.ADMIN,
      UserRole.PROGRAM_MANAGER,
      UserRole.ASSESSMENT_CREATOR,
      UserRole.HR,
      UserRole.EMPLOYEE,
    ],
  },

  // ========================================================================
  // ADMIN-ONLY ROUTES
  // ========================================================================
  {
    path: '/manage-users',
    component: ManageUsers,
    allowedRoles: [UserRole.ADMIN],
  },
  {
    path: '/manage-organizational-admins',
    component: ManageOrganizationalAdmins,
    allowedRoles: [UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/manage-organizational-admins/create',
    component: CreateOrgAdmin,
    allowedRoles: [UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/manage-organizational-admins/edit/:id',
    component: CreateOrgAdmin,
    allowedRoles: [UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/manage-organizational-admins/view/:id',
    component: ViewOrgAdmin,
    allowedRoles: [UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/daily-updates',
    component: DailyUpdatesFeedPage,
    allowedRoles: [UserRole.ADMIN],
  },

  // ========================================================================
  // PROGRAM MANAGER ROUTES
  // ========================================================================
  {
    path: '/users',
    component: UserManagement,
    allowedRoles: [UserRole.PROGRAM_MANAGER],
  },
  {
    path: '/recent-activities',
    component: RecentActivities,
    allowedRoles: [UserRole.ADMIN, UserRole.PROGRAM_MANAGER],
  },
  {
    path: '/reports',
    component: ReportsPage,
    allowedRoles: [UserRole.ADMIN, UserRole.PROGRAM_MANAGER],
  },
  {
    path: '/notifications',
    component: NotificationsPage,
    allowedRoles: [UserRole.ADMIN, UserRole.PROGRAM_MANAGER, UserRole.HR, UserRole.EMPLOYEE, UserRole.ASSESSMENT_CREATOR],
  },

  // ========================================================================
  // SESSIONS & WORKSHOPS (Program Manager, Admin, Assessment Creator)
  // ========================================================================
  {
    path: '/sessions',
    component: EnhancedProgramList,
    allowedRoles: [UserRole.ADMIN, UserRole.PROGRAM_MANAGER, UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/workshops',
    component: WorkshopsPage,
    allowedRoles: [UserRole.ADMIN, UserRole.PROGRAM_MANAGER, UserRole.ASSESSMENT_CREATOR, UserRole.EMPLOYEE],
  },
  {
    path: '/management-index/sessions/tasks/:programId',
    component: TaskManagement,
    allowedRoles: [UserRole.ADMIN, UserRole.PROGRAM_MANAGER, UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/management-index/sessions/categories',
    component: ChecklistManagement,
    allowedRoles: [UserRole.ADMIN, UserRole.PROGRAM_MANAGER, UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/management-index/sessions/checklist/:id',
    component: ChecklistPage,
    allowedRoles: [UserRole.ADMIN, UserRole.PROGRAM_MANAGER, UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/management-index/sessions/progress/:programId',
    component: ProgramProgressView,
    allowedRoles: [UserRole.ADMIN, UserRole.PROGRAM_MANAGER, UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/management-index/workshops/:programType',
    component: WorkshopTasksPage,
    allowedRoles: [UserRole.ADMIN, UserRole.PROGRAM_MANAGER, UserRole.ASSESSMENT_CREATOR, UserRole.EMPLOYEE],
  },

  // ========================================================================
  // HR ROUTES
  // ========================================================================
  {
    path: '/employees',
    component: EmployeesPage,
    allowedRoles: [UserRole.HR],
  },
  {
    path: '/employees/:id/edit',
    component: EmployeeEditPage,
    allowedRoles: [UserRole.HR],
  },
  {
    path: '/employees/:id/profile',
    component: EmployeeProfilePage,
    allowedRoles: [UserRole.HR],
  },
  {
    path: '/daily-tracker',
    component: DailyTrackerPage,
    allowedRoles: [UserRole.HR],
  },
  {
    // Daily Plan Board. Open to Employee as well as PM/HR/Admin because being a
    // manager is a data fact (employees.reporting_manager), not a role — the
    // backend scopes non-HR callers to their own reportees, so a non-manager sees
    // an empty board rather than someone else's data.
    path: '/hr/plan-reports/board',
    component: PlanBoardPage,
    allowedRoles: [
      UserRole.HR,
      UserRole.ADMIN,
      UserRole.PROGRAM_MANAGER,
      UserRole.EMPLOYEE,
    ],
  },
  {
    path: '/hr/plan-reports/period',
    component: PeriodReportPage,
    allowedRoles: [
      UserRole.HR,
      UserRole.ADMIN,
      UserRole.PROGRAM_MANAGER,
      UserRole.EMPLOYEE,
    ],
  },
  {
    path: '/hr/plan-reports/slippage',
    component: SlippagePage,
    allowedRoles: [
      UserRole.HR,
      UserRole.ADMIN,
      UserRole.PROGRAM_MANAGER,
      UserRole.EMPLOYEE,
    ],
  },
  {
    // Org-wide analytics is an HR/Admin view — a manager's slice of it is already
    // covered by the board and period reports, which auto-scope to their team.
    path: '/hr/plan-reports/analytics',
    component: AnalyticsPage,
    allowedRoles: [UserRole.HR, UserRole.ADMIN],
  },
  {
    path: '/daily-updates-feed',
    component: DailyUpdatesFeedPage,
    allowedRoles: [UserRole.HR],
  },
  {
    path: '/leaves',
    component: LeavesPage,
    allowedRoles: [UserRole.HR],
  },
  {
    path: '/holiday-management',
    component: HolidayManagement,
    allowedRoles: [UserRole.HR],
  },
  {
    path: '/leave-policy-settings',
    component: LeavePolicySettings,
    allowedRoles: [UserRole.HR, UserRole.ADMIN],
  },
  {
    path: '/settings',
    component: HRProfileSettings,
    allowedRoles: [UserRole.HR],
  },
  {
    path: '/payroll',
    component: Payroll,
    allowedRoles: [UserRole.HR],
  },
  {
    path: '/payroll/:id',
    component: PayrollDetails,
    allowedRoles: [UserRole.HR],
  },
  {
    path: '/payroll/:payrollId/add-employee',
    component: AddEmployeePayroll,
    allowedRoles: [UserRole.HR],
  },
  {
    path: '/payroll/:payrollId/employee/:employeePayrollId/edit',
    component: EditEmployeePayroll,
    allowedRoles: [UserRole.HR],
  },
  {
    path: '/payroll/:payrollId/employee/:employeePayrollId/view',
    component: ViewEmployeeSalarySlip,
    allowedRoles: [UserRole.HR],
  },
  {
    path: '/hr/payroll/:id',
    component: PayrollDetails,
    allowedRoles: [UserRole.HR],
  },
  {
    path: '/hr/jobs',
    component: JobPostingsPage,
    allowedRoles: [UserRole.HR],
  },
  {
    path: '/hr/jobs/new',
    component: JobPostingFormPage,
    allowedRoles: [UserRole.HR],
  },
  {
    path: '/hr/jobs/:id/edit',
    component: JobPostingFormPage,
    allowedRoles: [UserRole.HR],
  },
  {
    path: '/hr/recognitions',
    component: RecognitionsPage,
    allowedRoles: [UserRole.HR],
  },
  {
    path: '/wall-of-fame',
    component: WallOfFamePage,
    allowedRoles: [UserRole.HR, UserRole.EMPLOYEE, UserRole.ADMIN, UserRole.PROGRAM_MANAGER, UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/hr/applications',
    component: JobApplicationsPage,
    allowedRoles: [UserRole.HR],
  },

  // ========================================================================
  // PUBLIC CAREERS ROUTES (No Auth Required)
  // ========================================================================
  {
    path: '/careers',
    component: PublicJobsPage,
    allowedRoles: [],
    isPublic: true,
  },
  {
    path: '/careers/:id',
    component: PublicJobDetailPage,
    allowedRoles: [],
    isPublic: true,
  },
  {
    path: '/verify-payslip/:barcodeData',
    component: VerifyPayslip,
    allowedRoles: [],
    isPublic: true,
  },
  {
    path: '/verify-payslip',
    component: VerifyPayslip,
    allowedRoles: [],
    isPublic: true,
  },

  // ========================================================================
  // EMPLOYEE ROUTES
  // ========================================================================
  {
    // Attendance portal (plan / EOD) — every role logs attendance here.
    path: '/my-work',
    component: MyWork,
    allowedRoles: [
      UserRole.EMPLOYEE,
      UserRole.HR,
      UserRole.ADMIN,
      UserRole.PROGRAM_MANAGER,
      UserRole.ASSESSMENT_CREATOR,
    ],
  },
  {
    path: '/team-attendance',
    component: TeamAttendance,
    allowedRoles: [UserRole.EMPLOYEE],
  },
  {
    path: '/leave-portal',
    component: LeavePortal,
    allowedRoles: [
      UserRole.ADMIN,
      UserRole.PROGRAM_MANAGER,
      UserRole.ASSESSMENT_CREATOR,
      UserRole.HR,
      UserRole.EMPLOYEE,
    ],
  },
  {
    path: '/employee-resources',
    component: EmployeeResources,
    allowedRoles: [UserRole.EMPLOYEE],
  },

  // ========================================================================
  // ASSESSMENT CREATOR ROUTES
  // ========================================================================
  {
    path: '/assessment-dashboard',
    component: AssessmentDashboard,
    allowedRoles: [UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/manage-assessments',
    component: ManageAssessments,
    allowedRoles: [UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/create-assessment',
    component: CreateAssessment,
    allowedRoles: [UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/module-assessment-mapping',
    component: ModuleAssessmentMapping,
    allowedRoles: [UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/create-question',
    component: CreateQuestion,
    allowedRoles: [UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/assessment-results/:assessmentId',
    component: AssessmentResults,
    allowedRoles: [UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/assessment-users',
    component: Users,
    allowedRoles: [UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/manage-dimensions-modules',
    component: ManageDimensionsModules,
    allowedRoles: [UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/manage-assessment-eligibility',
    component: ManageAssessmentEligibility,
    allowedRoles: [UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/manage-assessment-domains',
    component: ManageAssessmentDomains,
    allowedRoles: [UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/assessment-creator/360-feedback/reporting-managers',
    component: ReportingManagerManagement,
    allowedRoles: [UserRole.ASSESSMENT_CREATOR],
  },
  {
    path: '/assessment-creator/180-feedback/reportee-management',
    component: ReporteeManagement,
    allowedRoles: [UserRole.ASSESSMENT_CREATOR],
  },
  // Removed - Flow changed: Use existing Create Assessment → Create Question flow for 360 feedback
  // {
  //   path: '/assessment-creator/360-feedback/create',
  //   component: Create360Feedback,
  //   allowedRoles: [UserRole.ASSESSMENT_CREATOR],
  // },
  // {
  //   path: '/assessment-creator/360-feedback/manage',
  //   component: Manage360Feedback,
  //   allowedRoles: [UserRole.ASSESSMENT_CREATOR],
  // },

  // ========================================================================
  // PRODUCTS ROUTES (Admin, HR)
  // ========================================================================
  {
    path: '/products/magnetix',
    component: MagnetixContactSubmissions,
    allowedRoles: [UserRole.ADMIN, UserRole.HR],
  },
];

/**
 * ============================================================================
 * HELPER FUNCTIONS
 * ============================================================================
 */

/**
 * Get all routes accessible to a user role
 */
export const getRoutesForRole = (userRole: string | undefined): RouteConfig[] => {
  if (!userRole) return [];
  return ROUTES.filter((route) => route.allowedRoles.includes(userRole as UserRole));
};

/**
 * Check if user has permission to access a route
 */
export const hasRoutePermission = (userRole: string | undefined, routePath: string): boolean => {
  if (!userRole) return false;

  // Find exact or dynamic route match
  const route = ROUTES.find((r) => {
    // Exact match
    if (r.path === routePath) return true;

    // Dynamic route match (e.g., /sessions/tasks/:id)
    const pattern = r.path.replace(/:[^/]+/g, '[^/]+');
    const regex = new RegExp(`^${pattern}$`);
    return regex.test(routePath);
  });

  if (!route) {
    console.log(`🚫 Route "${routePath}" not found in config`);
    return false;
  }

  const hasPermission = route.allowedRoles.includes(userRole as UserRole);
  console.log(
    `${hasPermission ? '✅' : '🚫'} Role "${userRole}" ${
      hasPermission ? 'CAN' : 'CANNOT'
    } access "${routePath}"`
  );

  return hasPermission;
};

/**
 * Get a specific route by path
 */
export const getRouteByPath = (path: string): RouteConfig | undefined => {
  return ROUTES.find((r) => r.path === path);
};